import { Header, NotificationCenter, Sidebar, Workspace } from "../components";
import React, { useEffect, useState } from "react";
import { Redirect, Route, Switch } from "react-router-dom";

import FormatTextdirectionLToRIcon from "@material-ui/icons/FormatTextdirectionLToR";
import FormatTextdirectionRToLIcon from "@material-ui/icons/FormatTextdirectionRToL";
import Hidden from "@material-ui/core/Hidden";
import { MobileBreakpoint } from "../styleVariables";
import SettingsIcon from "@material-ui/icons/Settings";
import SpeedDial from "@material-ui/lab/SpeedDial";
import SpeedDialAction from "@material-ui/lab/SpeedDialAction";
import SpeedDialIcon from "@material-ui/lab/SpeedDialIcon";
import WbSunnyIcon from "@material-ui/icons/WbSunny";
import classNames from "classnames";
import { makeStyles } from "@material-ui/core/styles";
import routes from "../routes";
import { useAppState } from "../components/AppProvider/AppProvider";
import { fetchDepartments, fetchPermissions, fetchRoles } from "../helpers/fetchers";
import { canAccessRoute, PERMISSIONS } from "../helpers/permissions";
import useMountEffect from "../mountEffect";
import LoginPage from "../pages/Login/LoginPage";
import { Box, Button, CircularProgress, Paper, Typography } from "@material-ui/core";
import { ExitToApp, Menu } from "@material-ui/icons";

const useStyles = makeStyles((theme) => ({
  panel: {
    position: "relative",
    overflow: "hidden",
    width: "100%",
    maxWidth: "100%",
    [theme.breakpoints.down("sm")]: {
      height: "auto",
      minHeight: "calc(100vh)",
    },
    [theme.breakpoints.down("xs")]: {
      minHeight: "calc(100vh)",
    },
    [theme.breakpoints.up("sm")]: {
      height: "auto",
      minHeight: "calc(100vh)",
    },
    display: "flex",
    flexDirection: "row",
    flexGrow: 1,
  },
  speedDial: {
    position: "absolute",
    bottom: theme.spacing(1) * 2,
    right: theme.spacing(1) * 3,
  },
}));

const Dashboard = ({ history }) => {
  const classes = useStyles();
  const [ctx, dispatch] = useAppState();
  const [opened, setOpened] = useState(() =>
    typeof window === "undefined"
      ? true
      : !window.matchMedia(`(max-width: ${MobileBreakpoint}px)`).matches
  );
  const [checkingSession, setCheckingSession] = useState(true);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [openSpeedDial, setOpenSpeedDial] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const apiKey = localStorage.getItem("apiKey");

        if (apiKey) {
          // A permissions failure (revoked/expired token, 5xx) throws and invalidates
          // the session rather than restoring it with an empty permission set, which
          // would strand the user on the "No Access" page.
          const { permissions, rbacEnabled } = await fetchPermissions(
            ctx?.baseUrl ?? "",
            apiKey
          );

          // Reference data other pages read from ctx (e.g. hierarchy department labels);
          // best-effort so missing read permission does not break session restore.
          let departments = [];
          let roles = [];
          try {
            [departments, roles] = await Promise.all([
              fetchDepartments(ctx?.baseUrl ?? "", apiKey),
              fetchRoles(ctx?.baseUrl ?? "", apiKey),
            ]);
          } catch (referenceError) {
            console.error(referenceError);
          }

          dispatch({ type: "apiKey", data: apiKey });
          dispatch({ type: "permissions", data: permissions });
          dispatch({ type: "rbacEnabled", data: rbacEnabled });
          dispatch({ type: "departments", data: departments });
          dispatch({ type: "roles", data: roles });
          dispatch({ type: "loggedIn", data: true });
        }
      } catch (error) {
        console.error(error);
        localStorage.removeItem("apiKey");
        dispatch({ type: "apiKey", data: null });
        dispatch({ type: "permissions", data: [] });
        dispatch({ type: "departments", data: [] });
        dispatch({ type: "roles", data: [] });
        dispatch({ type: "loggedIn", data: false });
      } finally {
        setCheckingSession(false);
      }
    })();
  }, []);

  const mediaMatcher = matchMedia(`(max-width: ${MobileBreakpoint}px)`);

  const resizeDispatch = () => {
    if (typeof Event === "function") {
      window.dispatchEvent(new Event("resize"));
    } else {
      const evt = window.document.createEvent("UIEvents");
      evt.initUIEvent("resize", true, false, window, 0);
      window.dispatchEvent(evt);
    }
  };

  const handleDrawerToggle = () => {
    setOpened(!opened);
    resizeDispatch();
  };

  const handleDrawerOpen = () => {
    setOpened(true);
    resizeDispatch();
  };

  const handleDrawerClose = () => {
    setOpened(false);
    resizeDispatch();
  };

  const handleNotificationToggle = () =>
    setNotificationsOpen(!notificationsOpen);

  const handleFullscreenToggle = () => {
    const element = document.querySelector("#root");
    const isFullscreen =
      document.webkitIsFullScreen || document.mozFullScreen || false;

    element.requestFullScreen =
      element.requestFullScreen ||
      element.webkitRequestFullScreen ||
      element.mozRequestFullScreen ||
      function () {
        return false;
      };
    document.cancelFullScreen =
      document.cancelFullScreen ||
      document.webkitCancelFullScreen ||
      document.mozCancelFullScreen ||
      function () {
        return false;
      };
    isFullscreen ? document.cancelFullScreen() : element.requestFullScreen();
  };

  const handleLogout = () => {
    // setAnchorEl(null);
    localStorage.removeItem("apiKey");
    if (dispatch) {
      dispatch({ type: "loggedIn", data: null });
      dispatch({ type: "apiKey", data: null });
      dispatch({ type: "permissions", data: [] });
      dispatch({ type: "departments", data: [] });
      dispatch({ type: "roles", data: [] });
    }
    history.replace("/");
  };

  const handleSpeedDialOpen = () => setOpenSpeedDial(true);

  const handleSpeedDialClose = () => setOpenSpeedDial(false);

  const accessibleRoutes = routes.items.filter((item) =>
    canAccessRoute(ctx.permissions, item)
  );
  // Landing / fallback target must be a real navigable route: exclude external
  // links, hidden entries, and parameterized paths (e.g. "/apps/:id") so we never
  // redirect to a literal ":id" URL.
  const firstAccessiblePath = accessibleRoutes.find(
    (item) =>
      item.type !== "external" && item.show && !item.path.includes(":")
  )?.path;

  useEffect(() => {
    if (
      ctx.loggedIn &&
      firstAccessiblePath &&
      history.location.pathname === "/no-access"
    ) {
      history.replace(firstAccessiblePath);
    }
  }, [ctx.loggedIn, firstAccessiblePath, history]);

  const noAccessPage = (
    <Box
      minHeight="calc(100vh - 64px)"
      display="flex"
      alignItems="center"
      justifyContent="center"
      p={3}
    >
      <Paper elevation={3}>
        <Box p={4} maxWidth={520} textAlign="center">
          <Typography variant="h5" gutterBottom>
            No Access
          </Typography>
          <Typography variant="body1" color="textSecondary">
            Akun Anda belum memiliki permission untuk mengakses menu apa pun.
            Silakan hubungi administrator agar role dan permission akun Anda
            dapat diatur terlebih dahulu.
          </Typography>
          <Box mt={3}>
            <Button
              variant="contained"
              color="secondary"
              startIcon={<ExitToApp />}
              onClick={handleLogout}
            >
              Logout
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );

  const getRoutes = (
    <Box>
      <Box display="flex" justifyContent="space-between" my={1} mx={3}>
        <Button onClick={handleDrawerToggle}>
          <Menu />
        </Button>
        <Box>
          <Button variant="contained" color="secondary" onClick={handleLogout}>
            Logout
          </Button>
        </Box>
      </Box>

      <Switch>
        <Route exact path="/no-access" render={() => noAccessPage} />
        {accessibleRoutes.map((item, index) =>
          item.type === "external" ? (
            <Route
              exact
              path={item.path}
              component={item.component}
              name={item.name}
              key={index}
            />
          ) : item.type === "submenu" ? (
            item.children.map((subItem) => (
              <Route
                exact
                path={`${item.path}${subItem.path}`}
                component={subItem.component}
                name={subItem.name}
              />
            ))
          ) : (
            <Route
              exact
              path={item.path}
              component={item.component}
              name={item.name}
              key={index}
            />
          )
        )}
        <Redirect to={firstAccessiblePath || "/no-access"} />
      </Switch>
    </Box>
  );

  useMountEffect(() => {
    if (mediaMatcher.matches) setOpened(false);
    mediaMatcher.addListener((match) => {
      setTimeout(() => {
        if (match.matches) setOpened(false);
        else setOpened(true);
      }, 300);
    });

    const unlisten = history.listen(() => {
      if (mediaMatcher.matches) setOpened(false);
      document.querySelector("#root > div > main").scrollTop = 0;
    });

    return () => {
      unlisten();
      mediaMatcher.removeListener((match) => {
        setTimeout(() => {
          if (match.matches) setOpened(false);
          else setOpened(true);
        }, 300);
      });
    };
  });

  return (
    <>
      {ctx.loggedIn ? (
        <>
          {" "}
          {/* <Header
            logoAltText="Primer Admin Template"
            logo={`${process.env.PUBLIC_URL}/static/images/logo.svg`}
            toggleDrawer={handleDrawerToggle}
            toogleNotifications={handleNotificationToggle}
            toggleFullscreen={handleFullscreenToggle}
          /> */}
          <div className={classNames(classes.panel, "theme-dark")}>
            <Sidebar
              routes={routes.items}
              opened={opened}
              toggleDrawer={handleDrawerToggle}
              openDrawer={handleDrawerOpen}
              closeDrawer={handleDrawerClose}
            />
            <Box display="flex" flexDirection="column" flexGrow={1} minWidth={0}>
              {ctx.loggedIn &&
                ctx.rbacEnabled === false &&
                ctx.permissions.includes(PERMISSIONS.ROLES_MANAGE) && (
                  <Box
                    bgcolor="#fff3cd"
                    color="#663c00"
                    px={2}
                    py={1}
                    fontSize={14}
                  >
                    RBAC is not enforced yet (preview mode). Map roles &
                    permissions in Access Management, then enable
                    enforcement.
                  </Box>
                )}
              <Workspace opened={opened}>{getRoutes}</Workspace>
            </Box>
            <NotificationCenter
              notificationsOpen={notificationsOpen}
              toogleNotifications={handleNotificationToggle}
            />
          </div>
        </>
      ) : checkingSession ? (
        <Box
          height="100vh"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <CircularProgress />
        </Box>
      ) : (
        <LoginPage />
      )}

      {/* <Hidden xsDown>
        <SpeedDial
          ariaLabel="Settings"
          className={classes.speedDial}
          icon={<SpeedDialIcon icon={<SettingsIcon />} />}
          onBlur={handleSpeedDialClose}
          onClose={handleSpeedDialClose}
          onFocus={handleSpeedDialOpen}
          onMouseEnter={handleSpeedDialOpen}
          onMouseLeave={handleSpeedDialClose}
          open={openSpeedDial}
        >
          <SpeedDialAction
            icon={<WbSunnyIcon />}
            tooltipTitle="Toggle light/dark theme"
            onClick={() => dispatch({ type: "type" })}
          />
          <SpeedDialAction
            icon={
              state.direction === "rtl" ? (
                <FormatTextdirectionLToRIcon />
              ) : (
                <FormatTextdirectionRToLIcon />
              )
            }
            tooltipTitle="Toggle LTR/RTL direction"
            onClick={() => dispatch({ type: "direction" })}
          />
        </SpeedDial>
      </Hidden> */}
    </>
  );
};

export default Dashboard;
